import PubSub from "pubsub-js";
import { pubsubEvents } from "../../components/helpers/socket/pubsubEvent";
import { getFieldLabel, isPriceField } from "../alertUtils";

/**
 * Helper to safely normalize AlertEvent payload keys from snake_case to camelCase
 */
const normalizePayload = (payload) => {
  if (!payload) return {};
  
  const getField = (obj, camel, snake) => {
    return obj[camel] !== undefined ? obj[camel] : obj[snake];
  };

  const normalized = {
    alertName: getField(payload, 'alertName', 'alert_name'),
    userId: getField(payload, 'userId', 'user_id'),
    userEmail: getField(payload, 'userEmail', 'user_email'),
    reqId: getField(payload, 'reqId', 'req_id'),
    productCodes: getField(payload, 'productCodes', 'product_codes'),
    timestamp: getField(payload, 'timestamp', 'timestamp'),
    source: getField(payload, 'source', 'source'),
    notes: getField(payload, 'notes', 'notes'),
    customData1: getField(payload, 'customData1', 'custom_data_1'),
    customData2: getField(payload, 'customData2', 'custom_data_2'),
    expiryType: getField(payload, 'expiryType', 'expiry_type'),
  };

  const rawMatchedConditions = getField(payload, 'matchedConditions', 'matched_conditions') || [];
  normalized.matchedConditions = rawMatchedConditions.map(c => ({
    securityId: getField(c, 'securityId', 'security_id'),
    alertField: getField(c, 'alertField', 'alert_field'),
    operator: getField(c, 'operator', 'operator'),
    operatorValue: getField(c, 'operatorValue', 'operator_value'),
    limitValue: getField(c, 'limitValue', 'limit_value'),
    triggeredValue: getField(c, 'triggeredValue', 'triggered_value'),
    instrumentSymbol: getField(c, 'instrumentSymbol', 'instrument_symbol'),
    instrumentExternalName: getField(c, 'instrumentExternalName', 'instrument_external_name'),
    logicOperator: getField(c, 'logicOperator', 'logic_operator'),
    isMatched: getField(c, 'isMatched', 'is_matched'),
    spreadStrategy: getField(c, 'spreadStrategy', 'spread_strategy'),
    spreadLegs: getField(c, 'spreadLegs', 'spread_legs'),
    targetSecurityId: getField(c, 'targetSecurityId', 'target_security_id'),
    targetField: getField(c, 'targetField', 'target_field'),
    targetInstrumentSymbol: getField(c, 'targetInstrumentSymbol', 'target_instrument_symbol'),
    targetInstrumentExternalName: getField(c, 'targetInstrumentExternalName', 'target_instrument_external_name'),
    targetConversionFactor: getField(c, 'targetConversionFactor', 'target_conversion_factor'),
    targetSpreadStrategy: getField(c, 'targetSpreadStrategy', 'target_spread_strategy'),
    targetSpreadLegs: getField(c, 'targetSpreadLegs', 'target_spread_legs'),
    productCode: getField(c, 'productCode', 'product_code'),
    targetProductCode: getField(c, 'targetProductCode', 'target_product_code'),
    conversionFactor: getField(c, 'conversionFactor', 'conversion_factor'),
    fractionalType: getField(c, 'fractionalType', 'fractional_type'),
    targetFractionalType: getField(c, 'targetFractionalType', 'target_fractional_type'),
    isPercentageChange: getField(c, 'isPercentageChange', 'is_percentage_change'),
    rawPercentageValue: getField(c, 'rawPercentageValue', 'raw_percentage_value'),
    baselineValue: getField(c, 'baselineValue', 'baseline_value'),
    originalOperator: getField(c, 'originalOperator', 'original_operator'),
  }));

  return normalized;
};

/**
 * This is the handler for received socket
 * messages from the alerting service.
 *
 * It is intended to match received actions to a dispatch event.
 */
export function alertingEventReciever(data) {
  if (
    data.service === "alerting" &&
    data.action === "notification" &&
    data.payload
  ) {
    try {
      const rawPayload =
        typeof data.payload === "string"
          ? JSON.parse(data.payload)
          : data.payload;
      
      const payloadObject = {
        ...rawPayload,
        ...normalizePayload(rawPayload),
      };
      
      const alertName = payloadObject.alertName?.replace(/_/g, " ");

      const getOperatorSymbol = (op) => {
        // Handle both Proto Enum values and potential string names
        if (typeof op === "string") {
          switch (op) {
            case "GTE":
              return ">=";
            case "GT":
              return ">";
            case "EQ":
              return "=";
            case "LTE":
              return "<=";
            case "LT":
              return "<";
            case "PERCENT_INCREASE":
              return "+%";
            case "PERCENT_DECREASE":
              return "-%";
            default:
              return op;
          }
        }
        switch (op) {
          case 0:
            return ">=";
          case 1:
            return ">";
          case 2:
            return "=";
          case 3:
            return "<=";
          case 4:
            return "<";
          case 5:
            return "+%";
          case 6:
            return "-%";
          default:
            return "";
        }
      };

      let toastMessage = null;
      const matchedConditions = payloadObject.matchedConditions || [];

      if (matchedConditions.length > 0) {
        // Filter for the condition that actually triggered the alert
        const matched = matchedConditions.filter(
          (c) => c.isMatched || c.triggeredValue !== undefined,
        );
        const firstCondition =
          matched.length > 0 ? matched[0] : matchedConditions[0];

        let instrumentName = payloadObject.customData1;
        if (!instrumentName || instrumentName === null) {
          instrumentName =
            firstCondition.instrumentSymbol ||
            payloadObject.symbol ||
            "Instrument";
        }

        const op =
          firstCondition.operatorValue !== undefined
            ? firstCondition.operatorValue
            : firstCondition.operator !== undefined
              ? firstCondition.operator
              : payloadObject.operator !== undefined
                ? payloadObject.operator
                : "GTE";

        const opSymbol = getOperatorSymbol(op);

        let limitValue = firstCondition.limitValue;
        if (limitValue === undefined) limitValue = payloadObject.limitValue;
        if (limitValue === undefined) limitValue = "[Target]";

        const fieldName =
          firstCondition.alertField ||
          firstCondition.field ||
          payloadObject.field;

        let triggeredValue = firstCondition.triggeredValue;
        if (triggeredValue === undefined) {
          if (fieldName && payloadObject[fieldName] !== undefined)
            triggeredValue = payloadObject[fieldName];
        }
        if (triggeredValue === undefined) triggeredValue = "[Value]";

        let readableField = "";
        if (fieldName === "bidPrice") readableField = "bid price";
        else if (fieldName === "bidSize" || fieldName === "bidPriceQty")
          readableField = "bid qty";
        else if (fieldName === "offerPrice") readableField = "offer price";
        else if (fieldName === "offerSize" || fieldName === "offerPriceQty")
          readableField = "offer qty";
        else if (fieldName === "totalVolume" || fieldName === "tradeVolume")
          readableField = "volume";
        else
          readableField = fieldName
            ? getFieldLabel(fieldName).toLowerCase()
            : "price";

        let title = "";
        let body = "";

        if (fieldName === "tradePrice" || !fieldName) {
          title = `${instrumentName} traded at price ${triggeredValue}`;
        } else if (fieldName === "tradePriceQty") {
          title = `${instrumentName} traded with quantity ${triggeredValue}`;
        } else if (fieldName === "totalVolume" || fieldName === "tradeVolume") {
          title = `${instrumentName} total ${readableField} is ${triggeredValue}`;
        } else {
          title = `${instrumentName} ${readableField} is ${triggeredValue}`;
        }

        if (opSymbol === ">" || opSymbol === ">=") {
          body = `Above your target of ${limitValue}`;
        } else if (opSymbol === "<" || opSymbol === "<=") {
          body = `Below your target of ${limitValue}`;
        } else if (opSymbol === "=") {
          body = `Equals your target of ${limitValue}`;
        } else {
          body = `Reached your target of ${limitValue}`;
        }

        toastMessage = `${title}\n${body}`;
      } else {
        // Fallback for old/legacy structure
        const {
          tradePrice,
          tradePriceQty,
          field,
          operator,
          limitValue,
          totalVolume,
          bidPrice,
          offerPrice,
          bidSize,
          offerSize,
        } = payloadObject;
        if (alertName) {
          let resolvedField = field;
          if (!resolvedField) {
            if (tradePrice !== undefined) resolvedField = "tradePrice";
            else if (tradePriceQty !== undefined)
              resolvedField = "tradePriceQty";
            else if (totalVolume !== undefined) resolvedField = "totalVolume";
            else if (bidPrice !== undefined) resolvedField = "bidPrice";
            else if (offerPrice !== undefined) resolvedField = "offerPrice";
            else if (bidSize !== undefined) resolvedField = "bidSize";
            else if (offerSize !== undefined) resolvedField = "offerSize";
          }

          if (resolvedField) {
            const isPrice = isPriceField(resolvedField);
            const triggeredVal = payloadObject[resolvedField];

            const formattedLimit =
              limitValue !== undefined ? limitValue : "[Target]";
            const formattedVal = isPrice
              ? typeof triggeredVal === "number"
                ? triggeredVal.toFixed(2)
                : triggeredVal !== undefined
                  ? triggeredVal
                  : "[Value]"
              : triggeredVal !== undefined
                ? new Intl.NumberFormat().format(triggeredVal)
                : "[Value]";

            let readableField = "";
            if (resolvedField === "bidPrice") readableField = "bid price";
            else if (
              resolvedField === "bidSize" ||
              resolvedField === "bidPriceQty"
            )
              readableField = "bid qty";
            else if (resolvedField === "offerPrice")
              readableField = "offer price";
            else if (
              resolvedField === "offerSize" ||
              resolvedField === "offerPriceQty"
            )
              readableField = "offer qty";
            else if (
              resolvedField === "totalVolume" ||
              resolvedField === "tradeVolume"
            )
              readableField = "volume";
            else readableField = getFieldLabel(resolvedField).toLowerCase();

            let title = "";
            let body = "";
            const opSym = getOperatorSymbol(operator);

            if (resolvedField === "tradePrice") {
              title = `${alertName} traded at price ${formattedVal}`;
            } else if (resolvedField === "tradePriceQty") {
              title = `${alertName} traded with quantity ${formattedVal}`;
            } else if (
              resolvedField === "totalVolume" ||
              resolvedField === "tradeVolume"
            ) {
              title = `${alertName} total ${readableField} is ${formattedVal}`;
            } else {
              title = `${alertName} ${readableField} is ${formattedVal}`;
            }

            if (opSym === ">" || opSym === ">=") {
              body = `Above your target of ${formattedLimit}`;
            } else if (opSym === "<" || opSym === "<=") {
              body = `Below your target of ${formattedLimit}`;
            } else if (opSym === "=") {
              body = `Equals your target of ${formattedLimit}`;
            } else {
              body = `Reached your target of ${formattedLimit}`;
            }

            toastMessage = `${title}\n${body}`;
          }
        }
      }

      if (toastMessage) {
        PubSub.publish(
          pubsubEvents.successKeys.ALERT_NOTIFICATION_TOAST,
          toastMessage,
        );
      } else {
        console.warn(
          "Received alert notification with missing or unknown data:",
          data,
        );
      }
    } catch (e) {
      console.error("Error processing alert notification:", e, data);
    }
  }
}
